import { Body, Header, SMPFlex, SMPText } from '@velinsi/smp-widgets-ui';
import ThemeProvider from '../../../../../packages/theme/ThemeProvider';
import { BaseWidgetProps, INormalizedStandingsGroup, ITopScorersConfig } from '../../../../../packages/types';
import useTopScorers from '../../hooks/useTopScorers';
import { normalizeTopScorers } from '../../normalizers/normalizeTopScorers';
import {
  defaultHandlePlayerClick,
  defaultHandleTeamClick,
  defaultHandleTournamentClick,
  getLabelValue,
} from '@sportal365/smp-widget-utils';
import useRootNodeRef from '../../../../../packages/hooks/useRootNodeRef';
import { useEffect, useMemo, useState } from 'react';
import useCompetition from '../../hooks/useCompetition';
import useStage from '../../hooks/useStage';
import { normalizeFootballCompetitionResponseData } from '../../normalizers/normalizeFootballCompetitionResponseData';
import { TableVariants } from '../../../../../packages/constants/sportType';

export type TopScorersWidgetProps = BaseWidgetProps<ITopScorersConfig>;

export const TopScorersWidget = ({ themes, config, subscribers, shadow, cuid }: TopScorersWidgetProps) => {
  const [competitionId, setCompetitionId] = useState<string | undefined>(config?.dataCompetition || '');
  const [seasonId, setSeasonId] = useState<string | undefined>('');
  const { ref } = useRootNodeRef();

  const {
    showWidget,
    handleClick,
    widgetData: { topScorers, isLoading },
  } = useTopScorers({
    subscribers,
    dataRefreshTime: config?.dataRefreshTime,
    dataSeason: seasonId,
    dataWidgetId: config?.dataWidgetId,
    dataWidgetSport: config?.dataWidgetSport,
    dataLimit: config?.dataLimit,
    dataStartPosition: config?.dataStartPosition,
    dataTeamIds: config?.dataTeamIds,
    dataWidgetCuid: cuid,
  });

  const {
    widgetData: { stage, isLoading: isStageLoading },
  } = useStage({
    subscribers,
    seasonId: config?.dataSeason,
    dataWidgetId: config?.dataWidgetId,
    dataWidgetSport: config?.dataWidgetSport,
    dataRefreshTime: config?.dataRefreshTime,
    dataWidgetCuid: cuid,
  });

  const { competition } = useCompetition({ competitionId: competitionId });

  useEffect(() => {
    if (!competitionId && stage) {
      setCompetitionId(stage.tournamentId);
    }
  }, [competitionId, stage]);

  useEffect(() => {
    if (competition) {
      const activeSeason = competition?.seasons?.find((season) => season.active === true)?.id;
      const foundSeason = competition.seasons?.find((season) => Number(season.id) == Number(config?.dataSeason))?.id;

      if (!foundSeason && !activeSeason) {
        // if their is no found season or active season
        const defaultSeason = competition?.seasons?.[0].id;
        setSeasonId(defaultSeason);
        return;
      }
      // if default season is not found the in the competition active one will be selected
      setSeasonId(foundSeason ? foundSeason : activeSeason);
    }
  }, [competition, config?.dataSeason]);

  const options = useMemo(
    () =>
      competition?.seasons &&
      competition.seasons.map((season) => ({
        label: season.name,
        value: season.id,
      })),
    [competition?.seasons],
  );

  const normalizedTopScorers = normalizeTopScorers({
    topScorers: topScorers ?? [],
    dataLabels: config?.dataLabels,
    dataEntityLinks: config?.dataEntityLinks,
    dataTeamNameType: config?.dataTeamNameType,
    dataHighlightEntities: config?.dataHighlightEntities,
    dataElements: config?.dataElements,
    displayImage: config?.dataImageDisplay,
  });

  const normalizedCompetition = useMemo(
    () => normalizeFootballCompetitionResponseData({ competition, dataEntityLinks: config?.dataEntityLinks }),
    [competition, config],
  );

  if (!showWidget) return null;

  const handlePlayerClick = (id: string | number, name: string) => defaultHandlePlayerClick(handleClick, id, name);

  const handleTeamClick = (id: string | number, name: string) => defaultHandleTeamClick(handleClick, { id, name });

  const handleSeasonChange = (value?: string) => setSeasonId(value);

  const handleTournamentClick = () =>
    defaultHandleTournamentClick(handleClick, Number(normalizedCompetition?.id), normalizedCompetition?.name);

  return (
    <ThemeProvider
      isLoading={isStageLoading || isLoading}
      shadow={shadow}
      themes={themes}
      dataTheme={config?.dataTheme}
      css={{ backgroundColor: '$primaryBackgroundColor' }}
      refForShadow={ref}
    >
      <Header
        logo={normalizedCompetition?.logo || normalizedCompetition?.tournamentImage}
        link={normalizedCompetition?.tournamentLink}
        urlTarget={normalizedCompetition?.urlTarget}
        title={competition?.name}
        secondaryTitle={competition?.country?.name}
        selectedOption={seasonId}
        options={options}
        handleSubscriptionClick={handleClick}
        handleChange={handleSeasonChange}
        handleClick={handleTournamentClick}
        displayMainHeader={config?.dataHeaderDisplay === true}
      />
      {config?.dataHeaderDisplay && (
        <SMPFlex align='end' css={{ backgroundColor: '$rowBackgroundColor', px: '$3', py: '$2_5' }}>
          <SMPText variant='contrast' size='3' weight='700' css={{ width: '100%' }}>
            {getLabelValue(config?.dataLabels?.top_scores, 'Top scores')}
          </SMPText>
        </SMPFlex>
      )}
      <Body
        data={normalizedTopScorers?.items.data as INormalizedStandingsGroup[]}
        handleClick={handlePlayerClick}
        handleTeamClick={handleTeamClick}
        noDataMessage={config?.dataLabels?.no_data}
        displayImage={config?.dataImageDisplay}
        tableVariant={TableVariants.STATS}
      />
    </ThemeProvider>
  );
};
